import { format } from 'date-fns'
import { Check, ClipboardList, ListChecks, Pencil, Plus, Trash2, UsersRound, X } from 'lucide-react'
import Linkify from '../ui/Linkify'
import SharedMemberPicker from '../ui/SharedMemberPicker'
import { STEP_KPI_ENABLED } from './cardDetailUtils'

function CardChecklistItem({
    t,
    checklist,
    item,
    isProcedure,
    userId,
    readOnly,
    canCreate,
    isAdmin,
    members,
    companyMembers,
    editingItemId,
    editingItemText,
    setEditingItemId,
    setEditingItemText,
    updateItem,
    onRefreshCards,
    handleToggleItem,
    stepDoneQty,
    stepBusyId,
    qtyPopoverId,
    qtyDraft,
    setQtyDraft,
    setQtyPopoverId,
    handleStepToggle,
    handleStepQuantity,
    expandedSharedItem,
    setExpandedSharedItem,
    sharedItemSearch,
    setSharedItemSearch,
    sharedItemPicker,
    setSharedItemPicker,
    toggleShared,
    updateSharedMembers,
    deleteItem,
}) {
    const isShared = item.is_shared
    const myCompletion = isShared && (item.completions || []).some((completion) => completion.user_id === userId)
    const isItemDone = isShared ? myCompletion : item.is_completed

    return (
        <div key={item.id} className={`checklist-item ${isShared ? 'checklist-item-shared' : ''}`}>
            {STEP_KPI_ENABLED && isProcedure && (Number(item.point_value) || 0) > 0 ? (() => {
                const { done, qty } = stepDoneQty(item)
                const pv = Number(item.point_value) || 0
                const busy = stepBusyId === item.id
                const showPop = qtyPopoverId === item.id
                const draftN = Math.max(1, parseInt(qtyDraft, 10) || 1)
                return (
                    <div className="step-kpi-control">
                        <div
                            className={`checklist-checkbox ${done ? 'checked' : ''} ${busy ? 'busy' : ''}`}
                            onClick={() => { if (!busy && !readOnly) handleStepToggle(item) }}
                        >
                            {done && <Check size={12} />}
                        </div>
                        <div className="step-kpi-meta">
                            <span className={`step-kpi-pill ${done ? 'earned' : ''}`}>
                                {done && qty > 1 ? `×${qty} = ${pv * qty}p` : `${pv}p`}
                            </span>
                            {!readOnly && (
                                <div className="step-kpi-more-wrap">
                                    <button
                                        type="button"
                                        className="step-kpi-more"
                                        onClick={(event) => { event.stopPropagation(); setQtyDraft(String(done ? qty : 1)); setQtyPopoverId(showPop ? null : item.id) }}
                                    >
                                        {t('card.step.moreTimes')}
                                    </button>
                                    {showPop ? (
                                        <div className="step-kpi-pop" onClick={(event) => event.stopPropagation()}>
                                            <span className="step-kpi-pop-label">{t('card.step.howManyTimes')}</span>
                                            <div className="step-kpi-pop-row">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={qtyDraft}
                                                    autoFocus
                                                    onChange={(event) => setQtyDraft(event.target.value)}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter') handleStepQuantity(item, qtyDraft)
                                                        if (event.key === 'Escape') setQtyPopoverId(null)
                                                    }}
                                                />
                                                <button type="button" className="step-kpi-pop-save" disabled={busy} onClick={() => handleStepQuantity(item, qtyDraft)}>
                                                    {t('common.save')}
                                                </button>
                                            </div>
                                            <span className="step-kpi-pop-calc">{draftN} × {pv} = <strong>{draftN * pv}p</strong></span>
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>
                )
            })() : (
                <div
                    className={`checklist-checkbox ${isItemDone ? 'checked' : ''}`}
                    onClick={() => handleToggleItem(checklist.id, item.id)}
                >
                    {isItemDone && <Check size={12} />}
                </div>
            )}
            <div className="checklist-item-body">
                {editingItemId === item.id ? (
                    <textarea
                        className="inline-input checklist-edit-input"
                        value={editingItemText}
                        autoFocus
                        rows={Math.max(2, Math.min(8, String(editingItemText || '').split('\n').length))}
                        onChange={(event) => setEditingItemText(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault()
                                if (editingItemText.trim() && editingItemText.trim() !== item.text) {
                                    updateItem(checklist.id, item.id, editingItemText.trim())
                                    onRefreshCards()
                                }
                                setEditingItemId(null)
                            }
                            if (event.key === 'Escape') setEditingItemId(null)
                        }}
                        onBlur={() => {
                            if (editingItemText.trim() && editingItemText.trim() !== item.text) {
                                updateItem(checklist.id, item.id, editingItemText.trim())
                                onRefreshCards()
                            }
                            setEditingItemId(null)
                        }}
                    />
                ) : (
                    <>
                        <Linkify
                            text={item.text}
                            className={`checklist-text ${isItemDone ? 'completed' : ''}`}
                            onDoubleClick={() => { setEditingItemId(item.id); setEditingItemText(item.text) }}
                        />
                        {isShared && (() => {
                            const sharedIds = (item.shared_member_ids || []).length > 0 ? item.shared_member_ids : members.map((member) => member.id)
                            const totalShared = sharedIds.length
                            const completedUserIds = new Set((item.completions || []).filter((completion) => sharedIds.includes(completion.user_id)).map((completion) => completion.user_id))
                            const completedCount = completedUserIds.size
                            const isExpanded = expandedSharedItem === item.id
                            const searchQ = isExpanded ? sharedItemSearch.toLowerCase() : ''
                            const sharedMembers = (companyMembers || []).filter((member) => sharedIds.includes(member.id))
                            const filtered = searchQ
                                ? sharedMembers.filter((member) => (member.full_name || '').toLowerCase().includes(searchQ))
                                : sharedMembers
                            const sortedMembers = [...filtered].sort((a, b) => {
                                const aDone = completedUserIds.has(a.id) ? 0 : 1
                                const bDone = completedUserIds.has(b.id) ? 0 : 1
                                return aDone - bDone || (a.full_name || '').localeCompare(b.full_name || '')
                            })

                            return totalShared > 0 ? (
                                <div className="csc-wrap">
                                    <button
                                        type="button"
                                        className={`checklist-shared-counter ${completedCount === totalShared ? 'all-done' : ''}`}
                                        onClick={(event) => {
                                            event.stopPropagation()
                                            setExpandedSharedItem(isExpanded ? null : item.id)
                                            setSharedItemSearch('')
                                        }}
                                    >
                                        {completedCount}/{totalShared}
                                    </button>
                                    {isExpanded && (
                                        <div className="csc-panel">
                                            <input
                                                className="csc-search"
                                                placeholder={t('card.checklist.itemSearchPlaceholder')}
                                                value={sharedItemSearch}
                                                onChange={(event) => setSharedItemSearch(event.target.value)}
                                                onClick={(event) => event.stopPropagation()}
                                                autoFocus
                                            />
                                            <div className="csc-list">
                                                {sortedMembers.map((member) => {
                                                    const done = completedUserIds.has(member.id)
                                                    return (
                                                        <div key={member.id} className={`csc-row ${done ? 'done' : ''}`}>
                                                            <span className={`csc-dot ${done ? 'done' : ''}`} />
                                                            <span className="csc-name">{member.full_name}</span>
                                                            {done && <Check size={12} className="csc-check" />}
                                                        </div>
                                                    )
                                                })}
                                                {filtered.length === 0 && (
                                                    <div className="csc-empty">{t('card.checklist.searchEmpty')}</div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : null
                        })()}
                        {!isShared && item.is_completed && item.completed_at && (
                            <span className="checklist-item-meta">
                                {format(new Date(item.completed_at), 'dd MMM, HH:mm')}
                                {item.completed_by && companyMembers?.find((member) => member.id === item.completed_by)?.full_name
                                    ? ` · ${companyMembers.find((member) => member.id === item.completed_by).full_name}`
                                    : ''}
                            </span>
                        )}
                    </>
                )}
            </div>
            {canCreate && (
                <div className="checklist-shared-wrapper">
                    <button
                        className={`checklist-shared-toggle ${isShared ? 'active' : ''}`}
                        onClick={() => {
                            if (isShared) {
                                toggleShared(checklist.id, item.id)
                            } else {
                                const remembered = Array.isArray(item.shared_member_ids)
                                    ? item.shared_member_ids.filter(Boolean)
                                    : []
                                if (remembered.length > 0) {
                                    toggleShared(checklist.id, item.id)
                                } else {
                                    setSharedItemPicker({ checklistId: checklist.id, itemId: item.id })
                                }
                            }
                        }}
                        title={isShared ? t('card.checklist.sharedToggleOn') : t('card.checklist.sharedToggleOff')}
                    >
                        <UsersRound size={12} />
                    </button>
                    {isShared && canCreate && (
                        <button
                            className="checklist-shared-edit"
                            onClick={() => setSharedItemPicker({ checklistId: checklist.id, itemId: item.id, editing: true })}
                            title={t('card.checklist.sharedEditMembers')}
                        >
                            <Pencil size={10} />
                        </button>
                    )}
                    {sharedItemPicker?.itemId === item.id && (
                        <SharedMemberPicker
                            allMembers={members.length > 0 ? members : (companyMembers || [])}
                            selected={isShared ? ((item.shared_member_ids || []).length > 0 ? item.shared_member_ids : members.map((member) => member.id)) : []}
                            onConfirm={(picked) => {
                                setSharedItemPicker(null)
                                if (sharedItemPicker.editing) {
                                    updateSharedMembers(checklist.id, item.id, picked)
                                } else {
                                    toggleShared(checklist.id, item.id, picked)
                                }
                            }}
                            onCancel={() => setSharedItemPicker(null)}
                        />
                    )}
                </div>
            )}
            {isAdmin && (
                <button className="checklist-item-delete" onClick={() => { deleteItem(checklist.id, item.id); onRefreshCards() }} title={t('card.checklist.deleteItemTooltip')}>
                    <X size={12} />
                </button>
            )}
        </div>
    )
}

function CardChecklistCard(props) {
    const {
        t,
        checklist,
        isProcedure = false,
        procedureByChecklistId,
        userId,
        isAdmin,
        setConfirmDeleteChecklist,
        canContribute,
        newItemTexts,
        setNewItemTexts,
        handleAddItem,
        addingItemLoading,
    } = props
    const items = checklist.checklist_items || []
    const done = items.filter((item) =>
        item.is_shared
            ? (item.completions || []).some((completion) => completion.user_id === userId)
            : item.is_completed
    ).length
    const total = items.length
    const linkedProcedure = isProcedure ? procedureByChecklistId[checklist.id] : null

    return (
        <div key={checklist.id} className={`card-detail-section cd-checklist-section ${isProcedure ? 'is-procedure' : 'is-manual'}`}>
            <div className="card-detail-section-title">
                <ListChecks size={15} />
                {checklist.name}
                <span className="cd-checklist-counter">{done}/{total}</span>
                {isProcedure ? <span className="cd-checklist-badge">{t('card.checklist.badge')}</span> : null}
                <div className="cd-spacer" />
                {isAdmin && (
                    <button className="edit-btn danger" onClick={() => setConfirmDeleteChecklist({ id: checklist.id, name: checklist.name, isProcedure })} title={t('card.checklist.deleteTooltip')}>
                        <Trash2 size={12} /> {isProcedure ? t('card.checklist.deleteInstance') : t('card.checklist.delete')}
                    </button>
                )}
            </div>
            {isProcedure && linkedProcedure?.title ? (
                <div className="cd-checklist-origin">
                    {t('card.checklist.template')} <strong>{linkedProcedure.title}</strong>
                </div>
            ) : null}

            {items.map((item) => (
                <CardChecklistItem
                    key={item.id}
                    {...props}
                    item={item}
                />
            ))}

            {canContribute && (
                <div className="checklist-add-item">
                    <textarea
                        value={newItemTexts[checklist.id] || ''}
                        onChange={(event) => setNewItemTexts((previous) => ({ ...previous, [checklist.id]: event.target.value }))}
                        placeholder={isProcedure ? t('card.checklist.addItemProcedure') : t('card.checklist.addItemManual')}
                        rows={1}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault()
                                handleAddItem(checklist.id)
                            }
                        }}
                    />
                    <button className="btn btn-primary btn-sm" onClick={() => handleAddItem(checklist.id)} disabled={addingItemLoading === checklist.id}>
                        <Plus size={12} /> {addingItemLoading === checklist.id ? t('card.checklist.adding') : t('card.checklist.addAction')}
                    </button>
                </div>
            )}
        </div>
    )
}

export default function CardChecklistTab({
    t,
    totalItems,
    progressPct,
    procedureChecklists,
    manualChecklists,
    checklists,
    addingChecklist,
    setAddingChecklist,
    newChecklistName,
    setNewChecklistName,
    handleAddChecklist,
    addingChecklistLoading,
    canEditDescription,
    ...cardProps
}) {
    return (
        <div>
            {totalItems > 0 && (
                <div className="checklist-progress cd-checklist-progress-wrap">
                    <span className="checklist-progress-pct">{progressPct}%</span>
                    <div className="checklist-progress-bar">
                        <div
                            className={`checklist-progress-fill ${progressPct === 100 ? 'complete' : ''}`}
                            style={{ width: `${progressPct}%` }}
                        />
                    </div>
                </div>
            )}

            {procedureChecklists.length > 0 && (
                <div className="cd-checklist-group">
                    <div className="cd-checklist-group-title">
                        <ClipboardList size={14} />
                        {t('card.checklist.proceduresGroup', { count: procedureChecklists.length })}
                    </div>
                    {procedureChecklists.map((checklist) => (
                        <CardChecklistCard
                            key={checklist.id}
                            t={t}
                            checklist={checklist}
                            isProcedure
                            {...cardProps}
                        />
                    ))}
                </div>
            )}

            {manualChecklists.length > 0 && (
                <div className="cd-checklist-group">
                    <div className="cd-checklist-group-title">
                        <ListChecks size={14} />
                        {t('card.checklist.customGroup', { count: manualChecklists.length })}
                    </div>
                    {manualChecklists.map((checklist) => (
                        <CardChecklistCard
                            key={checklist.id}
                            t={t}
                            checklist={checklist}
                            isProcedure={false}
                            {...cardProps}
                        />
                    ))}
                </div>
            )}

            {checklists.length === 0 && (
                <div className="card-empty-tab">
                    <ListChecks size={28} />
                    <p>{t('card.checklist.emptyTab')}</p>
                </div>
            )}

            {addingChecklist ? (
                <div className="card-detail-section">
                    <input
                        className="inline-input"
                        value={newChecklistName}
                        onChange={(event) => setNewChecklistName(event.target.value)}
                        placeholder={t('card.checklist.namePlaceholder')}
                        autoFocus
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') handleAddChecklist()
                            if (event.key === 'Escape') setAddingChecklist(false)
                        }}
                    />
                    <div className="cd-inline-actions">
                        <button className="btn btn-primary btn-sm" onClick={handleAddChecklist} disabled={addingChecklistLoading}>{addingChecklistLoading ? t('card.checklist.adding') : t('card.checklist.addAction')}</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => setAddingChecklist(false)}>{t('common.cancel')}</button>
                    </div>
                </div>
            ) : canEditDescription ? (
                <button className="btn btn-secondary btn-sm cd-mt-8" onClick={() => setAddingChecklist(true)}>
                    <Plus size={13} /> {t('card.checklist.addChecklist')}
                </button>
            ) : null}
        </div>
    )
}
